import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, LoadingSpinner, Modal, ModalFooter } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import {
  displayValue,
  fieldLabel,
  insurerProductsService,
  type ProductVersion,
} from '@/services/insurerProducts';
import { formatDateTime } from '@/utils';

/**
 * #398: a product's change history, with one-click revert.
 *
 * Revert restores the CONTENT of an earlier version as a new version (it is
 * itself revertible); status and broker access are never restored. Hidden
 * without `products.edit` (the API refuses it too).
 */

const ACTION_LABELS: Record<ProductVersion['action'], string> = {
  create: 'Created',
  update: 'Edited',
  status_change: 'Status changed',
  revert: 'Reverted',
  rate_chart: 'Rate chart uploaded',
  broker_access: 'Broker access',
  delete: 'Deleted',
};

interface Conflict {
  field: string;
  detail: string;
}

export function ProductHistory({
  productId,
  canEdit,
  onReverted,
}: {
  productId: string;
  canEdit: boolean;
  onReverted?: () => void;
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<number | null>(null);
  const [target, setTarget] = useState<ProductVersion | null>(null);
  const [reason, setReason] = useState('');
  const [conflicts, setConflicts] = useState<Conflict[]>([]);

  const { data: versions = [], isLoading } = useQuery({
    queryKey: ['insurer-product-versions', productId],
    queryFn: () => insurerProductsService.versions(productId),
  });

  const revertMutation = useMutation({
    mutationFn: (skip: boolean) =>
      insurerProductsService.revert(productId, {
        version_no: target?.version_no,
        reason,
        skip_conflicts: skip,
      }),
    onSuccess: () => {
      toast.success(`Restored version ${target?.version_no}. It is live now.`);
      setTarget(null);
      setReason('');
      setConflicts([]);
      queryClient.invalidateQueries({ queryKey: ['insurer-product-versions', productId] });
      queryClient.invalidateQueries({ queryKey: ['insurance-policies'] });
      queryClient.invalidateQueries({ queryKey: ['insurer-product', productId] });
      onReverted?.();
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: { conflicts?: Conflict[] } } } })
        .response?.data;
      const found = data?.error?.conflicts;
      if (found?.length) {
        setConflicts(found);
        return;
      }
      toast.error(getErrorMessage(err));
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <LoadingSpinner />
      </div>
    );
  }

  const current = versions[0];
  const previous = versions.find((v) => current && v.version_no === current.version_no - 1);

  const openRevert = (version: ProductVersion) => {
    setTarget(version);
    setReason('');
    setConflicts([]);
  };

  return (
    <div className="space-y-3">
      {canEdit && previous && (
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={() => openRevert(previous)}>
            Revert to previous version
          </Button>
        </div>
      )}
      {versions.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No changes recorded yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/40">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600 dark:text-gray-300">
                  Version
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600 dark:text-gray-300">
                  When
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600 dark:text-gray-300">
                  Who
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600 dark:text-gray-300">
                  What
                </th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {versions.map((v) => (
                <Fragment key={v.id}>
                  <tr
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    onClick={() => setExpanded(expanded === v.version_no ? null : v.version_no)}
                  >
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">v{v.version_no}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                      {formatDateTime(v.created_at)}
                    </td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                      {v.changed_by_display}
                      {v.changed_by_role && (
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          {v.changed_by_role.replace(/_/g, ' ')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {ACTION_LABELS[v.action] ?? v.action}
                      </span>
                      {v.reverted_from_version ? ` (to v${v.reverted_from_version})` : ''}
                      {v.changed_fields.length > 0 && (
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          {v.changed_fields.map(fieldLabel).join(', ')}
                        </span>
                      )}
                      {v.reason && (
                        <span className="block text-xs italic text-gray-500 dark:text-gray-400">
                          {v.reason}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canEdit &&
                        current &&
                        v.version_no < current.version_no &&
                        v.action !== 'delete' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              openRevert(v);
                            }}
                          >
                            Revert to this version
                          </Button>
                        )}
                    </td>
                  </tr>
                  {expanded === v.version_no && Object.keys(v.diff).length > 0 && (
                    <tr className="bg-gray-50 dark:bg-gray-900/40">
                      <td colSpan={5} className="px-3 py-2">
                        <ul className="space-y-1 text-xs text-gray-700 dark:text-gray-300">
                          {Object.entries(v.diff).map(([field, [from, to]]) => (
                            <li key={field}>
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {fieldLabel(field)}:
                              </span>{' '}
                              {displayValue(from)} &rarr; {displayValue(to)}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={!!target}
        onClose={() => setTarget(null)}
        title={`Revert to version ${target?.version_no ?? ''}`}
      >
        {target && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              This restores the product&apos;s content as it was in version {target.version_no}. It
              goes live for brokers immediately. Status and broker access are not changed.
            </p>
            {conflicts.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                <p className="font-medium">Some parts cannot be restored:</p>
                <ul className="mt-1 list-disc pl-5">
                  {conflicts.map((c) => (
                    <li key={c.field}>
                      {fieldLabel(c.field)}: {c.detail}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <label
              htmlFor="revert-reason"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Reason
            </label>
            <textarea
              id="revert-reason"
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={() => setTarget(null)}>
            Cancel
          </Button>
          {conflicts.length > 0 ? (
            <Button
              onClick={() => revertMutation.mutate(true)}
              isLoading={revertMutation.isPending}
            >
              Restore everything else
            </Button>
          ) : (
            <Button
              onClick={() => revertMutation.mutate(false)}
              isLoading={revertMutation.isPending}
            >
              Revert
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
}
